import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./SavedPage.module.css";
import {
  getFavorites,
  addToFavorites,
  removeFromFavorites,
} from "../../requests/favorites/favorites";
import type { CatalogItem } from "../../types/catalog.ts";
import { ReactComponent as HeartIcon } from "../../assets/transperentHeart.svg";
import { addToCart } from "../../requests/cart/cart";
import { useNotificationStore } from "../../store/NotificationStore";
import { useCartStore } from "../../store/useCartStore";
import { BackButton } from "../../components/BackButton/BackButton.tsx";
import SupportQuick from "../../components/SupportQuick/SupportQuick";
import Loader from "../../components/Loader/Loader";
import copyButtonIcon from "../../assets/copyButton.png";
import { ReactComponent as HeartNavIcon } from "../../assets/heart.svg";
import { ReactComponent as CatalogNavIcon } from "../../assets/catalog.svg";
import { ReactComponent as BucketNavIcon } from "../../assets/bucket.svg";
import { ReactComponent as SupportNavIcon } from "../../assets/support.svg";
import { useNavigate } from "react-router-dom";
import { useNavMenuStore } from "../../store/useNavMenuStore";
import type { MouseEvent } from "react";

const WEB_APP_BASE_URL = "https://menpoint.net";

const SavedPage = () => {
  const navigate = useNavigate();
  const tg = window.Telegram?.WebApp;
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > 500);
  const [favorites, setFavorites] = useState<CatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageSlideDirection, setImageSlideDirection] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<number[]>([]);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const imageLoadCache = useRef<Set<string>>(new Set());
  const setNotification = useNotificationStore(
    (state) => state.setNotification,
  );
  const { updateCartCount, cartItemsCount } = useCartStore();
  const { setVisibility: setNavMenuVisibility } = useNavMenuStore();

  useEffect(() => {
    const onResize = () => {
      setIsDesktop(window.innerWidth > 500);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setNavMenuVisibility(false);
      return;
    }
    setNavMenuVisibility(true);
  }, [isDesktop, setNavMenuVisibility]);

  useEffect(() => {
    return () => {
      setNavMenuVisibility(true);
    };
  }, [setNavMenuVisibility]);

  useEffect(() => {
    const fetchFavorites = async () => {
      const startTime = Date.now();
      const minLoadingTime = 2000; // 3 секунды

      try {
        setIsLoading(true);
        const favoritesResponse = await getFavorites();
        console.log("favoritesResponse:", favoritesResponse);
        const favoriteItems = favoritesResponse.data || [];
        setFavorites(favoriteItems);
        const ids = favoriteItems.map((item: CatalogItem) => item.id);
        setFavoriteIds(ids);
      } catch (error) {
        console.error("Ошибка при загрузке избранного:", error);
      } finally {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        if (remainingTime > 0) {
          setTimeout(() => {
            setIsLoading(false);
          }, remainingTime);
        } else {
          setIsLoading(false);
        }
      }
    };
    fetchFavorites();
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isModalOpen]);

  const formatPrice = (price: number): string => {
    return Math.round(price)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  // Функция для расчета цены со скидкой
  const calculateDiscountedPrice = (item: CatalogItem): number | null => {
    if (!item.discount || !item.discount.value) {
      return null;
    }

    const { type, value } = item.discount;

    if (type === "percentage") {
      const discountAmount = (item.price.amount * value) / 100;
      return item.price.amount - discountAmount;
    }

    if (type === "fixed") {
      return Math.max(0, item.price.amount - value);
    }

    return null;
  };

  const handleAddToCartClick = async () => {
    if (!selectedItem) return;
    if (!selectedSize) {
      setNotification({
        type: "Error",
        message: "Пожалуйста, выберите размер",
      });
      return;
    }

    try {
      const originalPrice = selectedItem.price.amount;
      const discountPercent =
        selectedItem.discount &&
        selectedItem.discount.type === "percentage" &&
        selectedItem.discount.value
          ? selectedItem.discount.value
          : 0;

      await addToCart(
        selectedItem.id,
        selectedSize,
        originalPrice,
        discountPercent,
      );
      setNotification({
        type: "Success",
        message: "Товар успешно добавлен в корзину",
      });

      setIsModalOpen(false);
      await updateCartCount();
    } catch (error) {
      console.error("Ошибка при добавлении в корзину:", error);
    }
  };

  const handleHeartClick = async (item: CatalogItem) => {
    try {
      setFavorites((prev) => prev.filter((fav) => fav.id !== item.id));
      setFavoriteIds((prev) => prev.filter((id) => id !== item.id));
      await removeFromFavorites(item.id);
    } catch (error) {
      console.error("Ошибка при удалении из избранного:", error);
    }
  };

  const handleSwipe = () => {
    if (
      touchStartX.current === null ||
      touchEndX.current === null ||
      !selectedItem
    ) {
      return;
    }

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        showNextImage();
      } else {
        showPreviousImage();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const preloadImage = (src?: string): Promise<void> => {
    if (!src) return Promise.resolve();
    if (imageLoadCache.current.has(src)) return Promise.resolve();

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        imageLoadCache.current.add(src);
        resolve();
      };
      img.onerror = () => {
        resolve();
      };
      img.src = src;
      if (img.complete) {
        imageLoadCache.current.add(src);
        resolve();
      }
    });
  };

  const switchToImage = async (nextIndex: number, direction: number) => {
    if (!selectedItem) return;
    const images = selectedItem.view_details.images;
    if (!images[nextIndex] || nextIndex === currentImageIndex) return;

    await preloadImage(images[nextIndex]);
    setImageSlideDirection(direction);
    setCurrentImageIndex(nextIndex);
  };

  const showNextImage = () => {
    if (!selectedItem) return;
    const imagesLength = selectedItem.view_details.images.length;
    const nextIndex =
      currentImageIndex === imagesLength - 1 ? 0 : currentImageIndex + 1;
    void switchToImage(nextIndex, 1);
  };

  const showPreviousImage = () => {
    if (!selectedItem) return;
    const imagesLength = selectedItem.view_details.images.length;
    const nextIndex =
      currentImageIndex === 0 ? imagesLength - 1 : currentImageIndex - 1;
    void switchToImage(nextIndex, -1);
  };

  useEffect(() => {
    if (!selectedItem) return;

    const images = selectedItem.view_details.images;
    if (!images.length) return;

    void preloadImage(images[0]);
    void Promise.all(images.map((src) => preloadImage(src)));
  }, [selectedItem]);

  const handleManagerContactClick = () => {
    const managerUrl = "https://t.me/menpoint1";
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(managerUrl);
      return;
    }
    window.open(managerUrl, "_blank", "noopener,noreferrer");
  };

  const handleCopyProductLink = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!selectedItem) return;

    const url = new URL("catalog", `${WEB_APP_BASE_URL.replace(/\/+$/, "")}/`);
    url.searchParams.set("open_product", String(selectedItem.id));
    const link = url.toString();

    const copyWithFallback = () => {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = link;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setNotification({
          type: "Success",
          message: "Ссылка скопирована",
        });
      } catch (err) {
        console.error("Clipboard fallback failed:", err);
        setNotification({
          type: "Error",
          message: "Не удалось скопировать ссылку",
        });
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(link)
        .then(() => {
          setNotification({
            type: "Success",
            message: "Ссылка скопирована",
          });
        })
        .catch(() => {
          copyWithFallback();
        });
    } else {
      copyWithFallback();
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div
      className={`${styles.conatiner} ${
        !isDesktop ? styles.mobileTopOffset : ""
      }`}
    >
      {!isDesktop && <BackButton />}
      {!isDesktop && <SupportQuick />}
      {isDesktop && (
        <div className={styles.desktopToolbar}>
          <div className={styles.desktopTopNav}>
            <button
              className={`${styles.desktopTopNavButton} ${styles.desktopTopNavIconOnlyButton}`}
              onClick={() => navigate("/catalog")}
              aria-label="Catalog"
            >
              <CatalogNavIcon className={styles.desktopTopNavIcon} />
              <span>Избранное</span>
            </button>
            <button
              className={`${styles.desktopTopNavButton} ${styles.desktopTopNavButtonActive} ${styles.desktopTopNavIconOnlyButton}`}
              onClick={() => navigate("/saved")}
              aria-label="Saved"
            >
              <HeartNavIcon className={styles.desktopTopNavIcon} />
              <span>Каталог</span>
            </button>
            <button
              className={`${styles.desktopTopNavButton} ${styles.desktopTopNavIconOnlyButton}`}
              onClick={handleManagerContactClick}
              aria-label="Manager"
            >
              <SupportNavIcon className={styles.desktopTopNavIcon} />
              <span>Корзина ({cartItemsCount})</span>
            </button>
            <button
              className={`${styles.desktopTopNavButton} ${styles.desktopTopNavIconOnlyButton}`}
              onClick={() => navigate("/bucket")}
              aria-label="Cart"
            >
              <BucketNavIcon className={styles.desktopTopNavIcon} />
              {cartItemsCount > 0 && (
                <span className={styles.desktopTopNavBadge}>
                  {cartItemsCount}
                </span>
              )}
              <span>Менеджер</span>
            </button>
          </div>
        </div>
      )}
      <div className={styles.savedPageTitle}>Избранное</div>
      {favorites.length > 0 ? (
        <div className={styles.savedPageItems}>
          <AnimatePresence mode="popLayout">
            {favorites
              .filter((favorite) => favorite.status === "active")
              .map((favorite, index) => {
                const discountedPrice = calculateDiscountedPrice(favorite);
                return (
                  <motion.div
                    key={favorite.id}
                    className={styles.savedPageItem}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{
                      opacity: 0,
                      scale: 0.8,
                      x: -100,
                      transition: {
                        duration: 0.4,
                        ease: [0.25, 0.46, 0.45, 0.94],
                      },
                    }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.05,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    layout
                  >
                    <div className={styles.savedPageItemImage}>
                      <img
                        src={favorite.view_details.images[0]}
                        alt={favorite.view_details.title}
                      />
                    </div>
                    <div className={styles.savedPageItemInfo}>
                      <div className={styles.savedPageItemTitle}>
                        {favorite.view_details.title}
                      </div>
                      <div className={styles.savedPageItemPrice}>
                        {discountedPrice !== null ? (
                          <>
                            <span className={styles.savedPageItemDiscountPrice}>
                              {formatPrice(discountedPrice)} ₽
                            </span>
                            <span className={styles.savedPageItemOriginalPrice}>
                              {formatPrice(favorite.price.amount)} ₽
                            </span>
                          </>
                        ) : (
                          <span className={styles.savedPageItemDiscountPrice}>
                            {formatPrice(favorite.price.amount)} ₽
                          </span>
                        )}
                      </div>
                      <div className={styles.savedPageItemActions}>
                        <button
                          className={styles.savedPageItemButton}
                          onClick={() => {
                            setSelectedItem(favorite);
                            setCurrentImageIndex(0);
                            setSelectedSize(null);
                            setIsWishlisted(true);
                            setIsModalOpen(true);
                          }}
                        >
                          Выбрать
                        </button>
                        <div
                          className={styles.savedPageItemHeart}
                          onClick={() => handleHeartClick(favorite)}
                        >
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 0.3 }}
                          >
                            <HeartIcon
                              style={{
                                fill: "#ff0000",
                                stroke: "#ff0000",
                              }}
                            />
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
          </AnimatePresence>
        </div>
      ) : (
        <div className={styles.noItems}>Ваш список пуст, добавьте товары</div>
      )}

      {/* Модальное окно */}
      <AnimatePresence>
        {isModalOpen && selectedItem && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              className={styles.modalContent}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={styles.modalImageContainer}
                onTouchStart={(e) => {
                  touchStartX.current = e.touches[0].clientX;
                }}
                onTouchEnd={(e) => {
                  touchEndX.current = e.changedTouches[0].clientX;
                  handleSwipe();
                }}
                onMouseDown={(e) => {
                  touchStartX.current = e.clientX;
                }}
                onMouseUp={(e) => {
                  touchEndX.current = e.clientX;
                  handleSwipe();
                }}
              >
                <button
                  className={styles.modalCloseButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsModalOpen(false);
                  }}
                >
                  ×
                </button>
                {selectedItem.view_details.images.length > 1 && (
                  <>
                    <button
                      className={`${styles.modalImageArrow} ${styles.modalImageArrowLeft}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        showPreviousImage();
                      }}
                      aria-label="Previous image"
                    />
                    <button
                      className={`${styles.modalImageArrow} ${styles.modalImageArrowRight}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        showNextImage();
                      }}
                      aria-label="Next image"
                    />
                  </>
                )}
                <motion.img
                  key={`${selectedItem.id}-${currentImageIndex}`}
                  src={selectedItem.view_details.images[currentImageIndex]}
                  alt={selectedItem.view_details.title}
                  className={styles.modalImage}
                  initial={{
                    opacity: 0,
                    x: imageSlideDirection > 0 ? 28 : -28,
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{
                    opacity: 0,
                    x: imageSlideDirection > 0 ? -28 : 28,
                  }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              {selectedItem.view_details.images.length > 1 && (
                <div className={styles.modalDots}>
                  {selectedItem.view_details.images.map((_, index) => (
                    <button
                      key={index}
                      className={`${styles.modalDot} ${
                        currentImageIndex === index ? styles.modalDotActive : ""
                      }`}
                      onClick={() => {
                        const direction = index > currentImageIndex ? 1 : -1;
                        void switchToImage(index, direction);
                      }}
                      aria-label={`Перейти к изображению ${index + 1}`}
                    />
                  ))}
                </div>
              )}

              <div className={styles.modalDetailsColumn}>
                <div className={styles.modalItemTitleRow}>
                  <div className={styles.modalItemTitle}>
                    {selectedItem.view_details.title}
                  </div>
                  {isDesktop && (
                    <button
                      className={styles.modalTitleCloseButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsModalOpen(false);
                      }}
                      aria-label="Close"
                    >
                      <span aria-hidden="true">&#215;</span>
                    </button>
                  )}
                  {!isDesktop && (
                    <button
                      className={styles.modalItemButtonCopy}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!selectedItem) return;

                        const url = new URL(
                          "catalog",
                          `${WEB_APP_BASE_URL.replace(/\/+$/, "")}/`,
                        );
                        url.searchParams.set(
                          "open_product",
                          String(selectedItem.id),
                        );
                        const link = url.toString();

                        const copyWithFallback = () => {
                          try {
                            const textarea = document.createElement("textarea");
                            textarea.value = link;
                            textarea.style.position = "fixed";
                            textarea.style.left = "-9999px";
                            document.body.appendChild(textarea);
                            textarea.select();
                            document.execCommand("copy");
                            document.body.removeChild(textarea);
                            setNotification({
                              type: "Success",
                              message: "Ссылка скопирована",
                            });
                          } catch (err) {
                            console.error("Clipboard fallback failed:", err);
                            setNotification({
                              type: "Error",
                              message: "Не удалось скопировать ссылку",
                            });
                          }
                        };

                        if (
                          navigator.clipboard &&
                          navigator.clipboard.writeText
                        ) {
                          navigator.clipboard
                            .writeText(link)
                            .then(() => {
                              setNotification({
                                type: "Success",
                                message: "Ссылка скопирована",
                              });
                            })
                            .catch(() => {
                              copyWithFallback();
                            });
                        } else {
                          copyWithFallback();
                        }
                      }}
                    >
                      <img
                        src={copyButtonIcon}
                        alt="Скопировать ссылку"
                        className={styles.modalItemCopyIcon}
                      />
                    </button>
                  )}
                </div>
                <div className={styles.modalItemDescription}>
                  {selectedItem.view_details.description}
                </div>
                <div className={styles.modalItemSizes}>Выберите размер</div>
                <div className={styles.modalItemSizesList}>
                  {selectedItem.sizes.map((size: string) => (
                    <div
                      className={`${styles.modalItemSize} ${
                        selectedSize === size ? styles.modalItemSizeActive : ""
                      }`}
                      key={size}
                      onClick={() => {
                        setSelectedSize(size);
                        console.log("Размер выбранный:", size);
                      }}
                    >
                      {size}
                    </div>
                  ))}
                </div>
                <div className={styles.modalItemPriceRow}>
                  <div className={styles.modalItemPrice}>
                    {(() => {
                      const discountedPrice =
                        calculateDiscountedPrice(selectedItem);
                      return discountedPrice !== null ? (
                        <>
                          <span className={styles.savedPageItemDiscountPrice}>
                            {formatPrice(discountedPrice)}₽
                          </span>{" "}
                          <span className={styles.savedPageItemOriginalPrice}>
                            {formatPrice(selectedItem.price.amount)}₽
                          </span>
                        </>
                      ) : (
                        <span className={styles.savedPageItemDiscountPrice}>
                          {formatPrice(selectedItem.price.amount)}₽
                        </span>
                      );
                    })()}
                  </div>
                  {isDesktop && (
                    <button
                      className={`${styles.modalItemButtonCopy} ${styles.modalItemButtonCopyDesktop}`}
                      onClick={handleCopyProductLink}
                      aria-label="Скопировать ссылку"
                    >
                      <img
                        src={copyButtonIcon}
                        alt="Скопировать ссылку"
                        className={styles.modalItemCopyIcon}
                      />
                    </button>
                  )}
                </div>
                <div className={styles.modalItemButtons}>
                  <button
                    className={styles.modalItemButtonAddToCart}
                    onClick={handleAddToCartClick}
                  >
                    Добавить в корзину
                  </button>
                  <div
                    className={styles.modalItemButtonAddToWishlist}
                    onClick={async () => {
                      if (!selectedItem) return;

                      try {
                        if (isWishlisted) {
                          // Удаляем из избранного
                          setFavorites((prev) =>
                            prev.filter((fav) => fav.id !== selectedItem.id),
                          );
                          setFavoriteIds((prev) =>
                            prev.filter((id) => id !== selectedItem.id),
                          );
                          setIsWishlisted(false);
                          await removeFromFavorites(selectedItem.id);
                        } else {
                          // Добавляем обратно в избранное
                          // Проверяем, нет ли уже этого товара в списке
                          if (!favoriteIds.includes(selectedItem.id)) {
                            setFavorites((prev) => {
                              if (
                                prev.some((fav) => fav.id === selectedItem.id)
                              ) {
                                return prev;
                              }
                              return [...prev, selectedItem];
                            });
                            setFavoriteIds((prev) => {
                              if (prev.includes(selectedItem.id)) {
                                return prev;
                              }
                              return [...prev, selectedItem.id];
                            });
                          }
                          setIsWishlisted(true);
                          await addToFavorites(selectedItem.id);
                        }
                      } catch (error) {
                        console.error(
                          "Ошибка при изменении избранного:",
                          error,
                        );
                        // В случае ошибки откатываем изменения
                        if (isWishlisted) {
                          setFavorites((prev) => {
                            if (
                              prev.some((fav) => fav.id === selectedItem.id)
                            ) {
                              return prev;
                            }
                            return [...prev, selectedItem];
                          });
                          setFavoriteIds((prev) => {
                            if (prev.includes(selectedItem.id)) {
                              return prev;
                            }
                            return [...prev, selectedItem.id];
                          });
                          setIsWishlisted(true);
                        } else {
                          setFavorites((prev) =>
                            prev.filter((fav) => fav.id !== selectedItem.id),
                          );
                          setFavoriteIds((prev) =>
                            prev.filter((id) => id !== selectedItem.id),
                          );
                          setIsWishlisted(false);
                        }
                      }
                    }}
                  >
                    <motion.div
                      animate={{ scale: isWishlisted ? [1, 1.2, 1] : 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <HeartIcon
                        className={styles.modalItemHeartIcon}
                        style={{
                          fill: isWishlisted ? "#ff0000" : "transparent",
                          stroke: isWishlisted ? "#ff0000" : "#818181",
                        }}
                      />
                    </motion.div>
                    <span className={styles.modalItemWishlistText}>
                      Сохранить
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SavedPage;
